import React, {useEffect, useRef} from 'react';

// Splitting this up lets a 400kb chunk be split into 2 200kb chunks
// import videojs from 'video.js';

let videojs = null;
import(/* webpackPreload: true, webpackChunkName: "videojs" */'video.js').then(m => videojs = m.default);

const VideoPlayer = ({video, onReady, className, options = {}, onPlayed, onFinished, isOpen = false}) => {
    const videoRef = useRef(null);
    const playerRef = useRef(null);
    const triggeredOnPlayed = useRef(false);
    const triggeredOnFinished = useRef(false);
    const hasBeenFullscreen = useRef(false);

    const playerOptions = {
        autoplay: options?.autoplay ?? false,
        preload: options?.preload ? 'auto' : 'metadata',
        enableSmoothSeeking: true,
        playsinline: false,
        poster: video.posters.medium,
        fluid: options.fluid ?? false,
        controls: options.controls ?? true,
        // aspectRatio: `${video.dimensions.width}:${video.dimensions.height}`,
        sources: [{
            src: video.playlist,
            // type: video.type
        }],
    };

    useEffect(() => {
        if (!videojs) {
            return;
        }

        // Make sure Video.js player is only initialized once
        if (!playerRef.current) {
            // The Video.js player needs to be _inside_ the component el for React 18 Strict Mode.
            const videoElement = document.createElement("video-js");

            videoElement.classList.add('vjs-big-play-centered');
            videoElement.classList.add('vjs-theme-city');
            videoRef.current.appendChild(videoElement);

            const player = playerRef.current = videojs(videoElement, playerOptions, () => {
                onReady && onReady(player);
            });

            player.on('timeupdate', (e) => {
                const currentTime = player.currentTime();
                const duration = player.duration();
                const progress = (currentTime / duration) * 100;

                if (progress > 50 && !triggeredOnPlayed.current) {
                    triggeredOnPlayed.current = true;
                    onPlayed && onPlayed();
                }

                if (progress >= 99 && !triggeredOnFinished.current) {
                    triggeredOnFinished.current = true;
                    onFinished && onFinished();
                }
            });

            player.on('fullscreenchange', (e) => {
                if (!options.playsFullscreen) {
                    return;
                }

                // We need to skip the first fullscreenchange event, as it is triggered by the player
                if (player?.isFullscreen_ === true) {
                    hasBeenFullscreen.current = true;
                } else if (player?.isFullscreen_ === false && hasBeenFullscreen.current === true) {
                    player.pause();
                    onFinished && onFinished();
                }
            });

            videoRef.current.addEventListener('click', (e) => {
                e.preventDefault();
                e.stopPropagation();

                const player = playerRef.current;
                if (player.paused()) {
                    player.play();
                } else {
                    player.pause();
                }
            });

        // You could update an existing player in the `else` block here
        // on prop change, for example:
        } else {
            const player = playerRef.current;

            player.autoplay(playerOptions.autoplay);
            // player.src(playerOptions.sources);
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options, videoRef]);

    useEffect(() => {
        const player = playerRef.current;

        if (isOpen) {
            player?.play();
            if (options.playsFullscreen) {
                player.requestFullscreen();
            }
        } else {
            player?.pause();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);

    // Dispose the Video.js player when the functional component unmounts
    useEffect(() => {
        if (!videojs) {
            return;
        }

        const player = playerRef.current;

        return () => {
            if (player && !player.isDisposed()) {
                player.dispose();
                playerRef.current = null;
            }
        };
    }, [playerRef]);

    return <div data-vjs-player className={className}>
        <div ref={videoRef} />
    </div>;
}

export default VideoPlayer;
